import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, TrendingUp, Users } from 'lucide-react';
import {
  ContextHero,
  DataCard,
  DateBadge,
  EmptyStateModern,
  KpiCard,
  MoneyAmount,
  PageContainer,
  SectionTitle,
  SkeletonShimmer,
  StatusBadge,
  type UniversalStatus,
} from '@/components/design-system';
import { useFormationB2BInter } from '@/hooks/queries/useFormationB2B';

const STATUT_TONE: Record<string, UniversalStatus> = {
  planifiee: 'planifie',
  ouverte_inscriptions: 'en_cours',
  complete: 'fait',
  en_cours: 'en_cours',
  terminee: 'paye',
  annulee: 'annule',
  reportee: 'en_attente',
};

export function InterTab(): JSX.Element {
  const { data: inter = [], isLoading } = useFormationB2BInter();

  const actives = inter.filter((s) =>
    ['planifiee', 'ouverte_inscriptions', 'en_cours'].includes(s.statut),
  ).length;
  const totalParticipants = inter.reduce((s, x) => s + x.nb_participants, 0);
  const caCumule = inter.reduce((s, x) => s + Number(x.ca_cumule), 0);
  const tauxRempli = (() => {
    const tot = inter.reduce((s, x) => s + x.capacite_max, 0);
    if (tot === 0) return 0;
    return Math.round((totalParticipants / tot) * 100);
  })();

  return (
    <PageContainer>
      <div className="space-y-6">
        <ContextHero
          variant="formation_b2b"
          icon={<Building2 className="h-5 w-5" />}
          title="Sessions Inter-entreprises"
          subtitle="Sessions ouvertes à plusieurs clients en même temps — facturation distincte, charges mutualisées."
        />

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard
            variant="formation_b2b"
            label="Sessions inter actives"
            value={actives}
            icon={<Building2 className="h-4 w-4" />}
            loading={isLoading}
          />
          <KpiCard
            variant="formation_b2b"
            label="Total participants"
            value={totalParticipants}
            icon={<Users className="h-4 w-4" />}
            loading={isLoading}
          />
          <KpiCard
            variant="echeances"
            label="CA cumulé"
            value={<MoneyAmount amount={caCumule} variant="inline" />}
            icon={<TrendingUp className="h-4 w-4" />}
            loading={isLoading}
          />
          <KpiCard
            variant="formation_b2b"
            label="Taux remplissage inter"
            value={`${tauxRempli}%`}
            loading={isLoading}
          />
        </div>

        <SectionTitle>
          {inter.length} session{inter.length > 1 ? 's' : ''} inter
        </SectionTitle>

        {isLoading ? (
          <SkeletonShimmer variant="table" count={5} />
        ) : inter.length === 0 ? (
          <EmptyStateModern
            variant="formation_b2b"
            icon={<Building2 className="h-7 w-7" />}
            title="Aucune session inter-entreprises"
            description="Une session devient « inter » dès que plusieurs entreprises clientes inscrivent leurs salariés sur la même date."
          />
        ) : (
          <DataCard>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-left text-[10px] uppercase tracking-widest text-slate-400">
                  <tr>
                    <th className="py-2">Référence</th>
                    <th className="py-2">Formation</th>
                    <th className="py-2">Date</th>
                    <th className="py-2 text-right">Entreprises</th>
                    <th className="py-2 text-right">Participants</th>
                    <th className="py-2 text-right">Capacité</th>
                    <th className="py-2 text-right">CA cumulé</th>
                    <th className="py-2">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {inter.map((s, i) => (
                    <motion.tr
                      key={s.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: Math.min(i, 8) * 0.02 }}
                      className="border-t border-slate-100 hover:bg-slate-50"
                    >
                      <td className="py-2">
                        <Link
                          to={`/formation/sessions/${s.id}`}
                          className="font-mono text-violet-700 hover:underline"
                        >
                          {s.reference}
                        </Link>
                      </td>
                      <td className="py-2 font-semibold text-slate-800">
                        {s.formation_titre ?? '—'}
                      </td>
                      <td className="py-2">
                        <DateBadge date={s.date_debut} />
                      </td>
                      <td className="py-2 text-right font-bold tabular-nums">{s.nb_entreprises}</td>
                      <td className="py-2 text-right tabular-nums">{s.nb_participants}</td>
                      <td className="py-2 text-right tabular-nums text-slate-500">
                        {s.capacite_max}
                      </td>
                      <td className="py-2 text-right">
                        <MoneyAmount amount={s.ca_cumule} variant="inline" />
                      </td>
                      <td className="py-2">
                        <StatusBadge
                          status={STATUT_TONE[s.statut] ?? 'brouillon'}
                          label={s.statut.replace(/_/g, ' ')}
                        />
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </DataCard>
        )}
      </div>
    </PageContainer>
  );
}
